/**
 * @file Missions quotidiennes/hebdo/succes : compteurs de metriques, tirage des missions du jour/semaine, reclamation des recompenses.
 */
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { MetricId, MissionDef } from '@config/types';
import { dayKey, weekKey } from '../Clock';
import type { SimContext } from '../Context';
import { hashString, Rng } from '../rng/Rng';
import type { MissionProgress } from '../state/PlayerData';

export class MissionService {
  constructor(private readonly ctx: SimContext) {}

  private active(): MissionProgress[] {
    const m = this.ctx.data.missions;
    return [...m.daily, ...m.weekly, ...Object.values(m.achievements)];
  }

  /** Compteur a vie + progression de chaque mission active de cette metrique (O(missions actives)). */
  increment(metric: MetricId, amount: number): void {
    if (!(amount > 0) || !Number.isFinite(amount)) return;
    const ctx = this.ctx;
    const m = ctx.data.missions;
    m.lifetime[metric] += amount;
    for (const p of this.active()) {
      if (p.claimed || p.progress >= p.target) continue;
      if (ctx.index.mission(p.id).metric !== metric) continue;
      p.progress = Math.min(p.target, p.progress + amount);
      if (p.progress >= p.target) {
        ctx.bus.emit('MissionCompleted', { id: p.id });
        ctx.bus.emit('Toast', { key: 'toast.missionDone', kind: 'success' });
      }
    }
    ctx.bus.emit('MetricIncremented', { metric, amount });
  }

  private targetOf(def: MissionDef): number {
    if (!def.scaleWithStage) return def.target;
    const best = this.ctx.data.progress.bestStageValue;
    return Math.round(def.target * (1 + Math.floor(best / 10) * 0.5));
  }

  /** Tirage deterministe (graine = jour/semaine + graine de partie) : recharger ne change pas les missions du jour. */
  private draw(salt: string, kind: 'daily' | 'weekly', count: number): MissionProgress[] {
    const ctx = this.ctx;
    const pool = ctx.config.missions.filter((d) => d.kind === kind && (!d.unlock || ctx.services.unlocks.isUnlocked(d.unlock)));
    const rng = new Rng((hashString(salt) ^ ctx.data.meta.seed) >>> 0);
    return rng
      .shuffle([...pool])
      .slice(0, count)
      .map((d) => ({ id: d.id, progress: 0, target: this.targetOf(d), claimed: false }));
  }

  /** Resets temporels (jour, semaine) et creation des succes manquants. Appele chaque seconde et au chargement. */
  refresh(nowMs: number): void {
    const ctx = this.ctx;
    const m = ctx.data.missions;
    const dk = dayKey(nowMs);
    if (dk !== m.dayKey) {
      m.dayKey = dk;
      m.daily = this.draw(`d${dk}`, 'daily', ctx.config.pass.dailyMissionCount);
    }
    const wk = weekKey(nowMs);
    if (wk !== m.weekKey) {
      m.weekKey = wk;
      m.weekly = this.draw(`w${wk}`, 'weekly', ctx.config.pass.weeklyMissionCount);
    }
    for (const d of ctx.config.missions) {
      if (d.kind !== 'achievement' || m.achievements[d.id]) continue;
      m.achievements[d.id] = { id: d.id, progress: Math.min(d.target, m.lifetime[d.metric]), target: d.target, claimed: false };
    }
  }

  /** Reclamation unique d'une mission terminee (le bundle inclut l'XP de passe). */
  claim(id: string): Result {
    const ctx = this.ctx;
    const p = this.active().find((x) => x.id === id);
    if (!p || p.claimed || p.progress < p.target) return fail('nothingToClaim');
    p.claimed = true;
    ctx.tx.grantBundle(ctx.index.mission(id).reward, `mission:${id}`);
    ctx.bus.emit('MissionClaimed', { id });
    return okVoid;
  }
}
